import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useConsultorioCitas } from '../../context/ConsultorioCitasContext';
import { useConsultorioEquipo } from '../../hooks/useConsultorioEquipo';
import {
  getCitasConsultorio,
  getPacientesConsultorio,
  saveCita,
} from '../../data/consultorioData';
import {
  CITAS_CHANGED_EVENT,
} from './ConsultorioCitasNotifications';
import {
  buildCitaFormFromDaySlots,
  citaToForm,
  createEmptyCitaForm,
  getPacNombre,
  validateAndBuildCitaPayload,
} from '../../utils/citasHelpers';
import PacienteCitasFab from './PacienteCitasFab';
import PacienteCitasSidePanel from './PacienteCitasSidePanel';
import CitaFormModal from './CitaFormModal';
import { confirmCancelarCita, showModalWarning } from '../../utils/sweetAlert';

const HORIZON_DAYS = 30;

function isUpcomingActive(cita) {
  const estado = cita.estado || 'pendiente';
  if (estado === 'cancelada' || estado === 'realizada') return false;
  const start = new Date(cita.fechaHora);
  if (Number.isNaN(start.getTime())) return estado === 'pendiente' || estado === 'confirmada';
  const now = new Date();
  const horizon = new Date(now);
  horizon.setDate(horizon.getDate() + HORIZON_DAYS);
  return start >= now && start <= horizon;
}

export default function ConsultorioCitasFloating() {
  const { user } = useAuth();
  const location = useLocation();
  const routeParams = useParams();
  const { equipo, equipoIds } = useConsultorioEquipo(user);
  const {
    panel,
    showCitaModal,
    setShowCitaModal,
    openCitasPanel,
    closeCitasPanel,
    setListTab,
    setDiaSeleccionado,
    openNuevaCita,
    openCitaEnPanel,
    bindCitasHandlers,
  } = useConsultorioCitas();

  const [citas, setCitas] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [citaForm, setCitaForm] = useState(() => createEmptyCitaForm(user?.id));
  const [citaModalMode, setCitaModalMode] = useState('create');

  const idDoctor = user?.id;
  const routePacienteId = routeParams.id && location.pathname.includes('/consultorio/paciente/')
    ? routeParams.id
    : null;

  const loadCitas = useCallback(async () => {
    if (!idDoctor) {
      setCitas([]);
      setPacientes([]);
      return;
    }
    const ids = equipoIds.length ? equipoIds : [idDoctor];
    const [cList, pList] = await Promise.all([
      getCitasConsultorio(ids),
      getPacientesConsultorio(ids),
    ]);
    setCitas(Array.isArray(cList) ? cList : []);
    setPacientes(Array.isArray(pList) ? pList : []);
  }, [idDoctor, equipoIds]);

  useEffect(() => {
    loadCitas();
  }, [loadCitas]);

  useEffect(() => {
    const onChanged = () => loadCitas();
    window.addEventListener(CITAS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CITAS_CHANGED_EVENT, onChanged);
  }, [loadCitas]);

  const pendientes = useMemo(
    () =>
      (citas || [])
        .filter((c) => (c.estado || 'pendiente') === 'pendiente')
        .sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora)),
    [citas],
  );

  const proximas = useMemo(
    () =>
      (citas || [])
        .filter(isUpcomingActive)
        .sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora)),
    [citas],
  );

  const activas = useMemo(
    () =>
      (citas || [])
        .filter((c) => c.estado !== 'cancelada' && c.estado !== 'realizada')
        .sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora)),
    [citas],
  );

  const filterPacienteId = panel.filterPacienteId || routePacienteId || null;

  const citasListaPanel = useMemo(() => {
    let base = citas;
    if (filterPacienteId) {
      base = base.filter((c) => String(c.idPaciente) === String(filterPacienteId));
      return [...base].sort((a, b) => new Date(b.fechaHora) - new Date(a.fechaHora));
    }
    if (panel.listTab === 'pending') return pendientes;
    if (panel.listTab === 'upcoming') return proximas;
    return activas;
  }, [citas, filterPacienteId, panel.listTab, pendientes, proximas, activas]);

  const pacienteNombre = filterPacienteId
    ? getPacNombre(pacientes, filterPacienteId)
    : '';

  const panelTitle = filterPacienteId
    ? 'Citas del paciente'
    : panel.listTab === 'pending'
      ? 'Citas pendientes'
      : panel.listTab === 'upcoming'
        ? 'Próximas citas'
        : 'Gestión de citas';

  const openFab = useCallback(() => {
    openCitasPanel({
      view: 'list',
      listTab: 'pending',
      filterPacienteId: routePacienteId || null,
      lockPacienteId: routePacienteId || null,
    });
  }, [openCitasPanel, routePacienteId]);

  const handleAgendar = useCallback(() => {
    openNuevaCita({ pacienteId: filterPacienteId || routePacienteId || null });
  }, [openNuevaCita, filterPacienteId, routePacienteId]);

  const openNewForDate = useCallback(
    (date, startSlotLabel = null, lastIncludedSlotLabel = null) => {
      const lockId = panel.lockPacienteId || filterPacienteId || routePacienteId || null;
      const paciente = lockId ? pacientes.find((p) => String(p.id) === String(lockId)) : null;
      setCitaForm(
        buildCitaFormFromDaySlots(date, startSlotLabel, lastIncludedSlotLabel, idDoctor, lockId || '', equipo, paciente),
      );
      setCitaModalMode('create');
      setDiaSeleccionado(date);
      setShowCitaModal(true);
    },
    [idDoctor, equipo, pacientes, panel.lockPacienteId, filterPacienteId, routePacienteId, setDiaSeleccionado, setShowCitaModal],
  );

  const openEditarCita = useCallback(
    (cita) => {
      setCitaForm(citaToForm(cita));
      setCitaModalMode('edit');
      if (cita?.fechaHora) {
        const d = new Date(cita.fechaHora);
        d.setHours(0, 0, 0, 0);
        setDiaSeleccionado(d);
      }
      setShowCitaModal(true);
    },
    [setDiaSeleccionado, setShowCitaModal],
  );

  const openNuevaCitaModal = useCallback(
    (opts = {}) => {
      const lockId = opts.pacienteId ?? opts.formPatch?.idPaciente ?? null;
      const paciente = lockId && !String(lockId).startsWith('pending-')
        ? pacientes.find((p) => String(p.id) === String(lockId))
        : null;
      const base = createEmptyCitaForm(idDoctor, lockId || '', equipo, paciente);
      const patch = opts.formPatch && typeof opts.formPatch === 'object' ? opts.formPatch : {};
      setCitaForm({ ...base, ...patch });
      setCitaModalMode('create');
      if (opts.date || patch.fechaHora) {
        const d = new Date(opts.date || patch.fechaHora);
        d.setHours(0, 0, 0, 0);
        setDiaSeleccionado(d);
      } else {
        setDiaSeleccionado(null);
      }
      setShowCitaModal(true);
    },
    [idDoctor, equipo, pacientes, setDiaSeleccionado, setShowCitaModal],
  );

  const applyCitaFormPatch = useCallback(
    (patch = {}) => {
      if (!patch || typeof patch !== 'object') return;
      setCitaForm((f) => {
        const next = { ...patch };
        delete next._voiceMergeDay;
        if (patch._voiceMergeDay && f.fechaHora?.length >= 10) {
          const day = f.fechaHora.slice(0, 10);
          if (patch.fechaHora?.length >= 16) {
            next.fechaHora = `${day}T${patch.fechaHora.slice(11, 16)}`;
          }
          if (patch.fechaHoraFin?.length >= 16) {
            next.fechaHoraFin = `${day}T${patch.fechaHoraFin.slice(11, 16)}`;
          } else if (patch.fechaHora?.length >= 16 && !patch.fechaHoraFin) {
            /* keep existing fin day */
            if (f.fechaHoraFin?.length >= 16) {
              next.fechaHoraFin = `${day}T${f.fechaHoraFin.slice(11, 16)}`;
            }
          }
        }
        return { ...f, ...next };
      });
      if (patch.fechaHora) {
        const d = new Date(patch.fechaHora);
        d.setHours(0, 0, 0, 0);
        setDiaSeleccionado(d);
      }
      setShowCitaModal(true);
    },
    [setDiaSeleccionado, setShowCitaModal],
  );

  const changeEstadoCita = useCallback(
    async (cita, nuevoEstado) => {
      if (nuevoEstado === 'cancelada') {
        const ok = await confirmCancelarCita();
        if (!ok) return;
      }
      await saveCita({ ...cita, estado: nuevoEstado }, cita.idDoctor);
      await loadCitas();
      window.dispatchEvent(new CustomEvent(CITAS_CHANGED_EVENT));
    },
    [loadCitas],
  );

  const handleSaveCita = useCallback(async () => {
    const { error, payload, docId } = validateAndBuildCitaPayload(citaForm, idDoctor);
    if (error) {
      void showModalWarning('Datos incompletos', error);
      return;
    }
    await saveCita(payload, docId);
    await loadCitas();
    window.dispatchEvent(new CustomEvent(CITAS_CHANGED_EVENT));
    setShowCitaModal(false);
  }, [citaForm, idDoctor, loadCitas, setShowCitaModal]);

  const saveCitaForm = useCallback(async () => {
    await handleSaveCita();
  }, [handleSaveCita]);

  useLayoutEffect(() => {
    bindCitasHandlers({
      openEditarCita,
      changeEstadoCita,
      openNuevaCitaModal,
      applyCitaFormPatch,
      saveCitaForm,
    });
  }, [bindCitasHandlers, openEditarCita, changeEstadoCita, openNuevaCitaModal, applyCitaFormPatch, saveCitaForm]);

  if (!idDoctor) return null;

  return (
    <>
      <PacienteCitasFab
        count={pendientes.length}
        onClick={openFab}
        hidden={panel.show || showCitaModal}
        active={panel.show}
        label="Pendientes"
      />

      <PacienteCitasSidePanel
        show={panel.show}
        onHide={closeCitasPanel}
        title={panelTitle}
        pacienteNombre={pacienteNombre}
        citasPaciente={citasListaPanel}
        citasConsultorio={citas}
        pacientes={pacientes}
        equipo={equipo}
        pacienteId={filterPacienteId}
        diaSeleccionado={panel.diaSeleccionado}
        onDateChange={setDiaSeleccionado}
        onAgendar={handleAgendar}
        onEditarCita={openEditarCita}
        onCancelar={(c) => changeEstadoCita(c, 'cancelada')}
        onConfirmar={(c) => changeEstadoCita(c, 'confirmada')}
        onMarcarRealizada={(c) => changeEstadoCita(c, 'realizada')}
        onNewCitaFromSchedule={openNewForDate}
        onEditCitaFromSchedule={openEditarCita}
        onChangeEstadoFromSchedule={changeEstadoCita}
        initialView={panel.view}
        highlightCitaId={panel.highlightCitaId}
        listTab={panel.listTab}
        onListTabChange={setListTab}
        showListTabs={!filterPacienteId}
        listTabCounts={{
          pending: pendientes.length,
          upcoming: proximas.length,
          all: activas.length,
        }}
        pacientesLookup={pacientes}
        showPacienteEnTarjeta={!filterPacienteId}
        onVerDetalleCita={openCitaEnPanel}
        emptyListMessage={
          filterPacienteId
            ? 'Este paciente no tiene citas en esta vista.'
            : panel.listTab === 'pending'
              ? 'No hay citas pendientes por confirmar.'
              : 'No hay citas en esta vista.'
        }
      />

      <CitaFormModal
        show={showCitaModal}
        onHide={() => setShowCitaModal(false)}
        form={citaForm}
        setForm={setCitaForm}
        pacientes={pacientes}
        equipo={equipo}
        citas={citas}
        user={user}
        onSave={handleSaveCita}
        lockPacienteId={panel.lockPacienteId || filterPacienteId || undefined}
        diaSeleccionado={panel.diaSeleccionado}
        mode={citaModalMode}
        drawer
      />
    </>
  );
}
